import React, { useState } from 'react';
import '../styles/FormControls.css';
import './ViewVocableScreen.css';

import { Vocable } from './VocabularyScreen';
import {
  getPartOfSpeechLabel,
  getVerbTypeLabel,
  getVerbFormInfo,
  VERB_FORM_ORDER,
} from '../utils/vocabularyLabels';

interface ViewVocableScreenProps {
  vocable: Vocable;
  onBack: () => void;
  onEdit: (vocable: Vocable) => void;
  onDelete?: () => Promise<void> | void;
}

function ViewVocableScreen({ vocable, onBack, onEdit, onDelete }: ViewVocableScreenProps) {
  const { japanese, kana, english, partOfSpeech, verbType } = vocable;
  const isTransitive = vocable.transitive;
  const conjugations = vocable.conjugations ?? {};
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const conjugationEntries = Object.entries(conjugations).filter(([, value]) => Boolean(value));
  const sortedConjugations = conjugationEntries
    .map(([form, value]) => ({ form, value }))
    .sort((a, b) => {
      const aIndex = VERB_FORM_ORDER.indexOf(a.form as typeof VERB_FORM_ORDER[number]);
      const bIndex = VERB_FORM_ORDER.indexOf(b.form as typeof VERB_FORM_ORDER[number]);
      const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
      const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
      if (safeA !== safeB) {
        return safeA - safeB;
      }
      return a.form.localeCompare(b.form);
    });
  const partOfSpeechLabel = getPartOfSpeechLabel(partOfSpeech) ?? partOfSpeech;
  const verbTypeLabel = getVerbTypeLabel(verbType) ?? verbType;

  // Format next review date
  const formatNextReview = (nextReview?: string): string => {
    if (!nextReview) return '—';
    
    try {
      // Try parsing as ISO string first
      const date = new Date(nextReview);
      if (isNaN(date.getTime())) {
        // If not a valid date, try parsing as timestamp (seconds)
        const timestamp = typeof nextReview === 'string' ? parseInt(nextReview, 10) : nextReview;
        if (!isNaN(timestamp)) {
          // Convert from seconds to milliseconds if needed
          const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
          const dateFromTimestamp = new Date(ms);
          if (!isNaN(dateFromTimestamp.getTime())) {
            return dateFromTimestamp.toLocaleString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            });
          }
        }
        return '—';
      }
      
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  return (
    <div className="add-edit-vocable-screen view-vocable-screen">
      <div className="add-edit-vocable-container view-vocable-container">
        <div className="add-edit-vocable-header">
          <h2 className="add-edit-vocable-title">Vocable Details</h2>
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
        </div>

        <div className="vocable-details">
          <div className="form-group">
            <label className="form-label">Japanese (Kanji)</label>
            <div className="detail-value">{japanese || '—'}</div>
          </div>
          {kana && (
            <div className="form-group">
              <label className="form-label">Kana</label>
              <div className="detail-value">{kana}</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">English meanings</label>
            <div className="detail-value">
              {english?.length ? english.join(', ') : '—'}
            </div>
          </div>
          {partOfSpeech && partOfSpeech !== 'VERB' && (
            <div className="form-group">
              <label className="form-label">Part of speech</label>
              <div className="detail-value">{partOfSpeechLabel ?? '—'}</div>
            </div>
          )}
          {partOfSpeech === 'VERB' && (
            <div className="form-group">
              <label className="form-label">Part of speech</label>
              <div className="detail-value">
                {partOfSpeechLabel ?? '—'}
                {verbTypeLabel ? ` · ${verbTypeLabel}` : ''}
              </div>
            </div>
          )}
          {vocable.nextReview && (
            <div className="form-group">
              <label className="form-label">Next review</label>
              <div className="detail-value">{formatNextReview(vocable.nextReview)}</div>
            </div>
          )}
          {partOfSpeech === 'VERB' && (
            <>
                <div className="form-group">
                <div>
                    <label className="form-label">Transitive</label>
                    <div className="detail-value">
                    {typeof isTransitive === 'boolean' ? (isTransitive ? 'Yes' : 'No') : '—'}
                    </div>
                </div>
                </div>

                {sortedConjugations.length > 0 && (
                  <div className="form-group">
                    <label className="form-label">Conjugations</label>
                    <div className="conjugations-table-wrapper">
                      <table className="conjugations-table">
                        <thead>
                          <tr>
                            <th>Form</th>
                            <th>Conjugation</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedConjugations.map(({ form, value }) => {
                            const info = getVerbFormInfo(form);
                            return (
                              <tr key={form}>
                                <td>
                                  <div className="conjugation-form-label">{info.label}</div>
                                  {info.description && (
                                    <div className="conjugation-form-description">{info.description}</div>
                                  )}
                                </td>
                                <td>{value || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
            </>
            )}
        </div>

        <div className="button-row">
        <button className="btn btn-secondary" onClick={onBack}>
            Close
          </button>
          {onDelete && (
            <button
              className="btn btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              Delete
            </button>
          )}
          <button className="btn btn-primary" onClick={() => onEdit(vocable)}>
            Edit
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="confirm-overlay" role="dialog" aria-modal="true">
          <div className="confirm-dialog">
            <h3 className="confirm-title">Delete vocable?</h3>
            <p className="confirm-message">
              This action cannot be undone. Are you sure you want to delete this vocable?
            </p>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  if (!onDelete) {
                    setShowDeleteConfirm(false);
                    return;
                  }
                  try {
                    setIsDeleting(true);
                    await onDelete();
                    setShowDeleteConfirm(false);
                  } finally {
                    setIsDeleting(false);
                  }
                }}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewVocableScreen;

